import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import { Link } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import { Table, TableHeaderCell } from 'core';
import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { CopyButton } from 'core/components/copy-button';
import { SelectableRow } from 'core/components/table/selectable-row';
import { SelectableCell } from 'core/components/table/selectable-cell';
import { useToast } from 'core/components/toast';
import NoResults from '../no-results';
import useStyles from './wide-results.styles';

const NONE = '—';

const COLUMNS = ['Type', 'Name', 'ID', 'Status', 'Matched on', 'Date'];

// Rows the viewer may open link every cell but the ID, whose copy button can't sit inside a link.
const Cell = ({ to, children }) =>
  to ? <SelectableCell to={to}>{children}</SelectableCell> : <TableCell>{children}</TableCell>;

Cell.propTypes = {
  to: PropTypes.string,
  children: PropTypes.node.isRequired
};

Cell.defaultProps = {
  to: null
};

const Row = ({ row }) => {
  const classes = useStyles();
  const { notify } = useToast();

  const copyId = () => {
    const copied = copy(row.id);
    if (!copied) {
      notify({ type: 'error', message: `${row.id} couldn't be copied` });
    }
    return copied;
  };

  return (
    <SelectableRow id={row.key} className={classes.row} data-cy="row">
      <Cell to={row.to}>
        <Tag className={classes.typeTag}>{row.type}</Tag>
      </Cell>
      <Cell to={row.to}>
        <span className={classes.nameCell}>
          <span className={classes.name}>{row.name || NONE}</span>
          {row.location && <span className={classes.subline}>{row.location}</span>}
        </span>
      </Cell>
      <TableCell className={classes.idCell}>
        <span className={classes.id}>{row.id}</span>
        <CopyButton onCopy={copyId} aria-label={`Copy ${row.id}`} />
        {row.carrierLoginUrl && (
          <IconButton
            size="small"
            aria-label="Open the carrier's site"
            onClick={() => window.open(row.carrierLoginUrl, '_blank', 'noopener')}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
      <Cell to={row.to}>
        {row.status ? (
          <Tag className={classNames(classes.status, classes[row.status.tone])}>{row.status.label}</Tag>
        ) : (
          <span className={classes.muted}>{NONE}</span>
        )}
      </Cell>
      <Cell to={row.to}>
        {row.matchedOn ? (
          <span className={classes.matchedOn}>{row.matchedOn}</span>
        ) : (
          <span className={classes.muted}>{NONE}</span>
        )}
      </Cell>
      <Cell to={row.to}>
        <span className={classes.date}>{row.date || NONE}</span>
      </Cell>
    </SelectableRow>
  );
};

Row.propTypes = {
  row: PropTypes.object.isRequired
};

/**
 * The condensed layout's search results: one table layout for every record type, built from the rows in
 * search-rows.js. Paginated per type, or a capped preview with a "See all" link in the All view.
 */
const WideResults = ({ title, rows, totalHits, loading, page, onPageChange, seeAllTo, seeAllLabel, error }) => {
  const classes = useStyles();
  const paginate = Boolean(onPageChange);

  return (
    <Paper className={classes.panel} component="section" aria-label={title}>
      <div className={classes.panelHeader}>
        <Label type="subtitle" className={classes.title}>
          {title}
          {totalHits > 0 && <span className={classes.count}> · {totalHits}</span>}
        </Label>
        {seeAllTo && (
          <Link to={seeAllTo} className={classes.seeAll}>
            {seeAllLabel}
          </Link>
        )}
      </div>
      {Boolean(loading || rows.length) && (
        <Table
          selfContained={false}
          loading={loading}
          paginationEnabled={paginate}
          count={totalHits}
          rowsPerPage={rows.length}
          currentPage={page}
          onPageChange={paginate ? (_, nextPage) => onPageChange(nextPage) : null}
          header={COLUMNS.map((column) => (
            <TableHeaderCell key={column} columnId={column}>
              {column}
            </TableHeaderCell>
          ))}
          body={rows.map((row) => <Row key={row.key} row={row} />)}
        />
      )}
      {!loading && rows.length === 0 && <NoResults error={error} />}
    </Paper>
  );
};

WideResults.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  totalHits: PropTypes.number,
  loading: PropTypes.bool,
  page: PropTypes.number,
  // Omit for an unpaginated preview.
  onPageChange: PropTypes.func,
  seeAllTo: PropTypes.string,
  seeAllLabel: PropTypes.string,
  error: PropTypes.object
};

WideResults.defaultProps = {
  totalHits: 0,
  loading: false,
  page: 0,
  onPageChange: null,
  seeAllTo: null,
  seeAllLabel: null,
  error: null
};

export default WideResults;
